import Layout from "@components/Layout"
import Mascot, { MascotState } from "@components/Mascot"
import RadioInputButton from "@components/RadioInputButton"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form, useSubmit } from "react-router-dom"

type SimpleOption = string

interface OptionWithLabelKey {
  value: string
  labelKey: string
  descKey?: string
}

type OptionConfig = SimpleOption | OptionWithLabelKey

interface Props {
  translationKey: string
  options: readonly OptionConfig[]
  headingKey?: string
  subtitleKey?: string
  headerContent?: React.ReactNode
  /** @deprecated No longer used in Yazio-style design */
  backgroundImage?: string
}

const WRITING_DELAY_MS = 700

export default function SingleSelectPage({
  translationKey,
  options,
  headingKey = "heading",
  subtitleKey,
  headerContent,
}: Props) {
  const { t } = useTranslation()
  const submit = useSubmit()
  const [mascotState, setMascotState] = useState<MascotState>("idle")
  const formRef = useRef<HTMLFormElement>(null)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMascotState("writing")
    const form = event.currentTarget.form
    setTimeout(() => {
      if (form) submit(form)
    }, WRITING_DELAY_MS)
  }

  const heading = t(`pages.onboarding.${translationKey}.${headingKey}`)
  const subtitle = subtitleKey
    ? t(`pages.onboarding.${translationKey}.${subtitleKey}`)
    : undefined

  return (
    <Layout>
      {headerContent}

      <div className="mt-4 flex items-center gap-3">
        <Mascot state={mascotState} />
        <div className="speech-bubble flex-1">
          <h1 className="text-lg font-bold">{heading}</h1>
        </div>
      </div>

      {subtitle && <p className="custom-subheading mt-3">{subtitle}</p>}

      <Form className="custom-form" method="post" ref={formRef}>
        <div className="mt-6 flex grow flex-col justify-center space-y-3">
          {options.map((option) => {
            const isSimple = typeof option === "string"
            const value = isSimple ? option : option.value
            const labelKey = isSimple ? option : option.labelKey
            const descKey = isSimple ? undefined : option.descKey

            return (
              <RadioInputButton
                key={value}
                label={t(`pages.onboarding.${translationKey}.${labelKey}`)}
                name={translationKey}
                onChange={handleChange}
                text={
                  descKey
                    ? t(`pages.onboarding.${translationKey}.${descKey}`)
                    : undefined
                }
                value={value}
              />
            )
          })}
        </div>
      </Form>
    </Layout>
  )
}
